import React, { useMemo, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup
} from 'react-simple-maps';
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Dialog,
  AppBar,
  Toolbar
} from '@mui/material';
import { OpenInFull, Close as CloseIcon } from '@mui/icons-material';

// GeoJSON for USA Map
const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

// State center positions (lat/long) and names
const stateCenters = {
  AL: { coordinates: [-86.7509, 32.8067], name: 'AL' },
  AK: { coordinates: [-152.4044, 61.3707], name: 'AK' },
  AZ: { coordinates: [-111.93, 34.0489], name: 'AZ' },
  AR: { coordinates: [-92.1999, 34.9697], name: 'AR' },
  CA: { coordinates: [-119.4179, 36.7783], name: 'CA' },
  CO: { coordinates: [-105.7821, 39.5501], name: 'CO' },
  CT: { coordinates: [-72.7554, 41.6032], name: 'CT' },
  DE: { coordinates: [-75.5277, 38.9108], name: 'DE' },
  FL: { coordinates: [-81.5158, 27.6648], name: 'FL' },
  GA: { coordinates: [-82.9001, 32.1656], name: 'GA' },
  HI: { coordinates: [-155.5828, 19.8968], name: 'HI' },
  ID: { coordinates: [-114.742, 44.0682], name: 'ID' },
  IL: { coordinates: [-89.3985, 40.6331], name: 'IL' },
  IN: { coordinates: [-86.1349, 40.2672], name: 'IN' },
  IA: { coordinates: [-93.0977, 41.878], name: 'IA' },
  KS: { coordinates: [-98.4842, 39.0119], name: 'KS' },
  KY: { coordinates: [-84.27, 37.8393], name: 'KY' },
  LA: { coordinates: [-91.9623, 30.9843], name: 'LA' },
  MA: { coordinates: [-71.3824, 42.4072], name: 'MA' },
  MD: { coordinates: [-76.6413, 39.0458], name: 'MD' },
  ME: { coordinates: [-69.4455, 45.2538], name: 'ME' },
  MI: { coordinates: [-84.5361, 44.3148], name: 'MI' },
  MN: { coordinates: [-94.6859, 46.7296], name: 'MN' },
  MS: { coordinates: [-89.3985, 32.3547], name: 'MS' },
  MO: { coordinates: [-91.8318, 38.5739], name: 'MO' },
  MT: { coordinates: [-110.3626, 46.8797], name: 'MT' },
  NE: { coordinates: [-99.9018, 41.4925], name: 'NE' },
  NV: { coordinates: [-116.4194, 38.8026], name: 'NV' },
  NH: { coordinates: [-71.5724, 43.1939], name: 'NH' },
  NJ: { coordinates: [-74.4057, 40.0583], name: 'NJ' },
  NM: { coordinates: [-105.8701, 34.5199], name: 'NM' },
  NY: { coordinates: [-74.2179, 43.2994], name: 'NY' },
  NC: { coordinates: [-79.0193, 35.7596], name: 'NC' },
  ND: { coordinates: [-101.002, 47.5515], name: 'ND' },
  OH: { coordinates: [-82.9071, 40.4173], name: 'OH' },
  OK: { coordinates: [-97.0929, 35.4676], name: 'OK' },
  OR: { coordinates: [-120.5542, 43.8041], name: 'OR' },
  PA: { coordinates: [-77.1945, 41.2033], name: 'PA' },
  RI: { coordinates: [-71.4774, 41.5801], name: 'RI' },
  SC: { coordinates: [-81.1637, 33.8361], name: 'SC' },
  SD: { coordinates: [-99.9018, 43.9695], name: 'SD' },
  TN: { coordinates: [-86.5804, 35.5175], name: 'TN' },
  TX: { coordinates: [-99.9018, 31.9686], name: 'TX' },
  UT: { coordinates: [-111.0937, 39.321], name: 'UT' },
  VT: { coordinates: [-72.5778, 44.5588], name: 'VT' },
  VA: { coordinates: [-78.6569, 37.4316], name: 'VA' },
  WA: { coordinates: [-120.7401, 47.7511], name: 'WA' },
  WV: { coordinates: [-80.4549, 38.5976], name: 'WV' },
  WI: { coordinates: [-88.7879, 43.7844], name: 'WI' },
  WY: { coordinates: [-107.2902, 43.0759], name: 'WY' }
};

// Convert full state name → code
const stateNameToCode = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY'
};

// Bubble size scale
// const bubbleSize = count => Math.sqrt(count) * 0.9;
const bubbleSize = count => {
  if (count <= 0) return 4;
  if (count <= 5) return 5;
  if (count <= 10) return 6;
  if (count <= 20) return 8;
  if (count <= 50) return 10;
  if (count <= 100) return 14;
  if (count <= 200) return 16;
  if (count <= 300) return 18;
  if (count <= 400) return 20;
  if (count <= 500) return 22;
  if (count <= 600) return 24;

  return 26; // Max size
};

// Safe position update function to prevent crashes
const safePositionUpdate = (currentPosition, updates) => {
  try {
    return {
      ...currentPosition,
      ...updates,
      coordinates: Array.isArray(updates.coordinates)
        ? updates.coordinates
        : currentPosition.coordinates
    };
  } catch (error) {
    console.error('Position update error:', error);
    return currentPosition;
  }
};

// MapPanel UI Component
const MapPanel = ({
  title,
  data,
  onExpand,
  isExpanded = false,
  freightData,
  tabValue
}) => {
  const [tooltipContent, setTooltipContent] = useState('');
  const [position, setPosition] = useState({ coordinates: [0, 0], zoom: 1 });

  const handleMoveEnd = newPosition => {
    if (newPosition && typeof newPosition.zoom === 'number') {
      setPosition(safePositionUpdate(position, newPosition));
    }
  };

  const handleZoomIn = () => {
    setPosition(prev => ({
      ...prev, // Keep existing coordinates, only change zoom
      zoom: Math.min(prev.zoom * 1.5, 6)
    }));
  };

  const handleZoomOut = () => {
    setPosition(prev => ({
      ...prev, // Keep existing coordinates, only change zoom
      zoom: Math.max(prev.zoom / 1.5, 1)
    }));
  };

  const handleResetView = () => {
    setPosition({ coordinates: [0, 0], zoom: 1 });
  };

  const mapContent = (
    <Box
      sx={{
        marginTop: tabValue == 2 || tabValue == 3 ? -1.5 : 0,
        height: isExpanded
          ? 'calc(100vh - 100px)'
          : tabValue == 2 || tabValue == 3
          ? 'calc(100vh - 470px)'
          : 423,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <ComposableMap projection='geoAlbersUsa'>
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={handleMoveEnd}
          minZoom={1}
          maxZoom={isExpanded ? 8 : 6}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map(geo => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill='#E3E8EF'
                  stroke='#B0BAC5'
                  style={{
                    default: { outline: 'none' },
                    hover: { fill: '#CFD8DC', outline: 'none' },
                    pressed: { outline: 'none' }
                  }}
                />
              ))
            }
          </Geographies>

          {/* State Labels - Only show when zoomed in */}
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map(geo => {
                if (position.zoom < 2.5) return null; // Only show labels when zoomed in

                const centroid = geo.properties?.centroid;
                if (!centroid) return null;

                return (
                  <text
                    key={geo.rsmKey + '-label'}
                    x={centroid[0]}
                    y={centroid[1]}
                    textAnchor='middle'
                    fontSize={Math.max(8, 12 / position.zoom)}
                    fill='#37474F'
                    fontWeight='500'
                    style={{
                      pointerEvents: 'none',
                      opacity: 0.8
                    }}
                  >
                    {geo.properties.name}
                  </text>
                );
              })
            }
          </Geographies>

          {data.map((item, idx) => {
            const stateInfo = stateCenters[item.code];
            if (!stateInfo) return null;

            return (
              <Marker key={idx} coordinates={stateInfo.coordinates}>
                <g
                  onMouseEnter={() => {
                    const freightSpend = freightData[item.code] || 0;
                    const stateType = title.toLowerCase().includes('origin')
                      ? 'Origin'
                      : 'Destination';
                    setTooltipContent(
                      `<div style="padding: 8px;">
       <div>
         <div style="display: flex; justify-content: space-between; gap: 12px; margin-bottom: 2px;">
           <span style="font-weight: 600;">${stateType} State</span>
           <span style="font-weight: 600;">${stateInfo.name}</span>
         </div>
         <div style="display: flex; justify-content: space-between; gap: 12px; margin-bottom: 2px;">
           <span># of Shipments</span>
           <span style="font-weight: 600;">${item.shipments.toLocaleString()}</span>
         </div>
         <div style="display: flex; justify-content: space-between; gap: 12px;">
           <span>Freight Spend</span>
           <span style="font-weight: 600;">$${freightSpend.toLocaleString()}</span>
         </div>
       </div>
     </div>`
                    );
                  }}
                  onMouseLeave={() => {
                    setTooltipContent('');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <circle
                    r={
                      isExpanded
                        ? bubbleSize(item.shipments) * 1.5
                        : bubbleSize(item.shipments)
                    }
                    fill='rgba(33,150,243,0.75)'
                    stroke='#1976D2'
                    strokeWidth={isExpanded ? 3 : 2}
                    style={{
                      transition: 'all 0.2s ease'
                    }}
                  />
                  <text
                    textAnchor='middle'
                    y={5}
                    fontSize={isExpanded ? 14 : 10}
                    fill='#000'
                    fontWeight={600}
                    // Remove pointerEvents: 'none' to ensure mouse events work properly
                  >
                    {item.shipments}
                  </text>
                </g>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {/* Tooltip */}
      {tooltipContent && (
        <Box
          sx={{
            position: 'absolute',
            top: isExpanded ? 20 : 10,
            left: isExpanded ? 20 : 10,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: isExpanded ? '12px 16px' : '8px 12px',
            borderRadius: isExpanded ? '6px' : '4px',
            fontSize: isExpanded ? '16px' : '14px',
            fontWeight: 'bold',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
          }}
          dangerouslySetInnerHTML={{ __html: tooltipContent }}
        />
      )}

      {/* Zoom Controls */}
      {isExpanded && (
        <Box
          sx={{
            position: 'absolute',
            top: isExpanded ? 20 : 1,
            right: isExpanded ? 20 : 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '8px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
        >
          <IconButton
            size='medium'
            sx={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
            onClick={handleZoomIn}
          >
            <Typography fontSize='16px' fontWeight='bold'>
              +
            </Typography>
          </IconButton>
          <IconButton
            size='medium'
            sx={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
            onClick={handleZoomOut}
          >
            <Typography fontSize='16px' fontWeight='bold'>
              −
            </Typography>
          </IconButton>
        </Box>
      )}

      {/* Zoom Instructions */}
      {tabValue !== 2 && tabValue !== 3 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: isExpanded ? 20 : 60,
            left: isExpanded ? 20 : 10,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#666',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          Scroll to zoom • Drag to pan • Hover for details
        </Box>
      )}
    </Box>
  );

  // If expanded mode, just return the map content
  if (isExpanded) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* <Typography variant='h5' fontWeight='bold' gutterBottom>
          {title} - Detailed View
        </Typography> */}
        {mapContent}
      </Box>
    );
  }

  // Compact mode with Paper wrapper - Consistent with other charts
  return (
    <Paper
      elevation={1}
      style={{
        padding: 5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}
      >
        <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
          {title}
        </Typography>
        <IconButton
          size='small'
          sx={{
            backgroundColor: 'rgba(0,0,0,0.04)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.08)'
            }
          }}
          onClick={onExpand}
        >
          <OpenInFull fontSize='small' />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>{mapContent}</Box>
    </Paper>
  );
};

// Main BubbleMaps Component
const BubbleMaps = ({
  shipments,
  setExpandedChart,
  expandedChart,
  tabValue
}) => {
  const { originData, destinationData, originFreight, destinationFreight } =
    useMemo(() => {
      const originCount = {};
      const destinationCount = {};
      const originFreightSpend = {};
      const destinationFreightSpend = {};

      shipments.forEach(s => {
        const oCode = stateNameToCode[s.originState] || s.originState;
        const dCode = stateNameToCode[s.destinationState] || s.destinationState;

        // Calculate net freight (shipment charges minus agency surcharge)
        const netFreight = s.quoteNetCharge || 0;

        if (oCode) {
          originCount[oCode] = (originCount[oCode] || 0) + 1;
          originFreightSpend[oCode] =
            (originFreightSpend[oCode] || 0) + netFreight;
        }
        if (dCode) {
          destinationCount[dCode] = (destinationCount[dCode] || 0) + 1;
          destinationFreightSpend[dCode] =
            (destinationFreightSpend[dCode] || 0) + netFreight;
        }
      });

      return {
        originData: Object.entries(originCount).map(([code, count]) => ({
          code,
          shipments: count
        })),
        destinationData: Object.entries(destinationCount).map(
          ([code, count]) => ({
            code,
            shipments: count
          })
        ),
        originFreight: originFreightSpend,
        destinationFreight: destinationFreightSpend
      };
    }, [shipments]);

  // If we're in expanded mode, show the dialog with the specific map
  if (
    expandedChart === 'originShipmentsMap' ||
    expandedChart === 'destinationShipmentsMap'
  ) {
    return (
      <Dialog
        fullScreen
        open={!!expandedChart}
        onClose={() => setExpandedChart(null)}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge='start'
              color='inherit'
              onClick={() => setExpandedChart(null)}
              aria-label='close'
            >
              <CloseIcon />
            </IconButton>
            {/* <Typography sx={{ ml: 2, flex: 1, color: '#ffffff !important' }} variant='h6' component='div'>
              {expandedChart === 'originShipmentsMap' 
                ? 'Origin Shipments - Detailed View' 
                : 'Destination Shipments - Detailed View'
              }
            </Typography> */}
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 1, height: 'calc(100vh - 64px)' }}>
          {expandedChart === 'originShipmentsMap' && (
            <MapPanel
              title='Origin Shipments'
              data={originData}
              isExpanded={true}
              onExpand={() => {}}
              freightData={originFreight}
            />
          )}
          {expandedChart === 'destinationShipmentsMap' && (
            <MapPanel
              title='Destination Shipments'
              data={destinationData}
              isExpanded={true}
              onExpand={() => {}}
              freightData={destinationFreight}
            />
          )}
        </Box>
      </Dialog>
    );
  }

  // Normal compact view
  return (
    <Grid
      container
      spacing={0.5}
      sx={{
        height: tabValue == 2 || tabValue == 3 ? '100%' : '43%'
      }}
    >
      {tabValue !== 3 && (
        <Grid item xs={12} md={12}>
          <MapPanel
            title='Origin'
            data={originData}
            onExpand={() => setExpandedChart('originShipmentsMap')}
            freightData={originFreight}
            tabValue={tabValue}
          />
        </Grid>
      )}

      {tabValue !== 2 && (
        <Grid item xs={12} md={12}>
          <MapPanel
            title='Destination'
            data={destinationData}
            onExpand={() => setExpandedChart('destinationShipmentsMap')}
            freightData={destinationFreight}
            tabValue={tabValue}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default BubbleMaps;
